import { useState, useEffect, useCallback, useMemo } from "react";
import TourValidationService from "../services/tourValidationService";

/**
 * Hook personalizado para manejar las validaciones del tour
 * @param {Object} formData - Datos del formulario
 * @param {Object} tourData - Datos del tour
 * @param {Object} pricing - Información de precios
 * @param {Array} models - Lista de modelos disponibles
 * @param {Array} vehicles - Lista de vehículos disponibles
 * @param {Array} gpsDevices - Lista de dispositivos GPS disponibles
 * @returns {Object} Estado y funciones relacionadas con las validaciones
 */
export const useTourValidation = (formData, tourData, pricing, models, vehicles, gpsDevices) => {
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  /**
   * Valida el formulario completo
   */
  const validateForm = useCallback(() => {
    if (!formData || !tourData) {
      setErrors({});
      setIsValid(false);
      return;
    }

    try {
      const validationResult = TourValidationService.validateForm(
        formData,
        tourData,
        pricing,
        models || [],
        vehicles || [],
        gpsDevices || []
      );

      setErrors(validationResult.errors);
      setIsValid(validationResult.isValid);

      // Generar advertencias
      const warningsList = TourValidationService.generateWarnings(formData, tourData, pricing);
      setWarnings(warningsList);

    } catch (err) {
      console.error("Error validating form:", err);
      setErrors({ validation: "Error al validar el formulario" });
      setIsValid(false);
    }
  }, [formData, tourData, pricing, models, vehicles, gpsDevices]);

  /**
   * Valida un campo específico en tiempo real
   * @param {string} fieldName - Nombre del campo a validar
   * @param {any} value - Valor del campo
   */
  const validateField = useCallback((fieldName, value) => {
    if (!formData) return null;

    const error = TourValidationService.validateField(formData, fieldName);
    
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return error;
  }, [formData]);

  /**
   * Valida las reglas de negocio específicas
   */
  const validateBusinessRules = useCallback(() => {
    if (!formData || !tourData) return {};

    return TourValidationService.validateBusinessRules(formData, tourData);
  }, [formData, tourData]);

  /**
   * Valida los permisos del usuario
   * @param {Array} userPermissions - Permisos del usuario
   */
  const validatePermissions = useCallback((userPermissions) => {
    return TourValidationService.validatePermissions(userPermissions);
  }, []);

  /**
   * Valida la disponibilidad de recursos
   */
  const validateResourceAvailability = useCallback(() => {
    if (!formData || !vehicles || !gpsDevices) return {};

    return TourValidationService.validateResourceAvailability(vehicles, gpsDevices, formData);
  }, [formData, vehicles, gpsDevices]);

  /**
   * Limpia los errores de un campo específico
   * @param {string} fieldName - Nombre del campo
   */
  const clearFieldError = useCallback((fieldName) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  /**
   * Limpia todos los errores
   */
  const clearAllErrors = useCallback(() => {
    setErrors({});
    setFieldErrors({});
    setWarnings([]);
    setIsValid(false);
  }, []);

  /**
   * Obtiene todos los errores combinados
   */
  const allErrors = useMemo(() => {
    return {
      ...errors,
      ...fieldErrors
    };
  }, [errors, fieldErrors]);

  /**
   * Verifica si hay errores críticos
   */
  const hasCriticalErrors = useMemo(() => {
    const criticalFields = [
      "vehicle.modelId",
      "vehicle.vehicleId",
      "payment.amount",
      "discounts.discount",
      "discounts.rewards"
    ];

    return criticalFields.some(field => allErrors[field]);
  }, [allErrors]);

  /**
   * Obtiene el resumen de validación
   */
  const validationSummary = useMemo(() => {
    const totalErrors = Object.keys(allErrors).length;
    const totalWarnings = warnings.length;

    return {
      isValid,
      totalErrors,
      totalWarnings,
      hasCriticalErrors,
      errorFields: Object.keys(allErrors),
      warningMessages: warnings
    };
  }, [isValid, allErrors, warnings, hasCriticalErrors]);

  /**
   * Verifica si un campo específico es válido
   * @param {string} fieldName - Nombre del campo
   */
  const isFieldValid = useCallback((fieldName) => {
    return !allErrors[fieldName];
  }, [allErrors]);

  /**
   * Obtiene el mensaje de error de un campo
   * @param {string} fieldName - Nombre del campo
   */
  const getFieldError = useCallback((fieldName) => {
    return allErrors[fieldName] || null;
  }, [allErrors]);

  /**
   * Verifica si el formulario puede ser enviado
   */
  const canSubmit = useMemo(() => {
    return isValid && !hasCriticalErrors;
  }, [isValid, hasCriticalErrors]);

  // Validar formulario cuando cambian las dependencias
  useEffect(() => {
    if (formData && tourData) {
      validateForm();
    } else {
      setErrors({});
      setWarnings([]);
      setIsValid(false);
    }
  }, [formData, tourData, pricing, models, vehicles, gpsDevices, validateForm]);

  return {
    // Estado
    errors: allErrors,
    warnings,
    isValid,
    fieldErrors,

    // Funciones de validación
    validateForm,
    validateField,
    validateBusinessRules,
    validatePermissions,
    validateResourceAvailability,

    // Funciones de limpieza
    clearFieldError,
    clearAllErrors,

    // Funciones de consulta
    isFieldValid,
    getFieldError,
    canSubmit,

    // Resumen
    validationSummary
  };
}; 


// WEBPACK FOOTER //
// ./src/hooks/useTourValidation.js